import { createFileRoute } from "@tanstack/react-router";
import { useEffect } from "react";
import { CoachesPage } from "@/components/coaches/CoachesPage";
import { listCoachPosts } from "@/lib/blog.functions";
import careersCss from "@/careers/careers.css?url";
import { SITE_CHROME_LINKS } from "@/components/site/site-chrome-head";
import { COACHES_ALTERNATES } from "@/components/coaches/coaches-alternates";

// Fonts + the .cm-* chrome stylesheet, so the shared <CareersNav> header
// renders styled on /coaches (same set the careers section loads).
const CHROME_LINKS = [
  { rel: "stylesheet", href: careersCss },
];

// /coaches — RU canonical. AZ and EN live at /az/coaches and
// /en/coaches as dedicated route files (parity with privacy).
//
// Locale routing: TanStack Start's server.handlers.GET model returns
// a Response and can't "fall through" to the React component, so for
// React-rendered routes we do the locale redirect client-side. The
// /privacy route uses the server-side variant because it serves
// static HTML strings; /coaches uses React, hence the difference.
//
// Trade-off: a brief flash of the RU canonical page before the
// redirect fires for users with a non-RU yt-lang cookie. Acceptable
// for a low-traffic info page; if the flash becomes objectionable
// later we can move to a beforeLoad-based redirect.

function CoachesCanonical() {
  useEffect(() => {
    if (typeof document === "undefined") return;
    const m = document.cookie.match(/(?:^|;\s*)yt-lang=(ru|az|en)\b/);
    if (m && m[1] !== "ru") {
      window.location.replace(`/${m[1]}/coaches`);
    }
  }, []);
  const { posts } = Route.useLoaderData();
  return <CoachesPage lang="ru" posts={posts} />;
}

export const Route = createFileRoute("/coaches")({
  // Статьи коучей в блоге на языке страницы; сбой чтения — страница без них.
  loader: async () => ({ posts: await listCoachPosts({ data: { lang: "ru" } }).catch(() => ({})) }),
  head: () => ({
    meta: [
      { title: "Коучи Yolutap — индивидуальные карьерные консультации" },
      {
        name: "description",
        content:
          "Сертифицированные коучи Yolutap — индивидуальные карьерные консультации после диагностики. Помогаем подростку перевести результаты в осознанные шаги к будущему.",
      },
      { property: "og:title", content: "Коучи Yolutap — индивидуальные карьерные консультации" },
      {
        property: "og:description",
        content:
          "Сертифицированные специалисты, которые помогают подростку перевести результаты диагностики в осознанные шаги к будущему.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://yolutap.com/coaches" },
      { property: "og:image", content: "https://yolutap.com/photos/teen_boy_hero.jpg" },
      { property: "og:locale", content: "ru_RU" },
      { name: "twitter:title", content: "Коучи Yolutap — индивидуальные карьерные консультации" },
      { name: "twitter:description", content: "Сертифицированные специалисты, которые помогают подростку перевести результаты диагностики в осознанные шаги к будущему." },
    ],
    links: [{ rel: "canonical", href: "https://yolutap.com/coaches" }, ...COACHES_ALTERNATES, ...CHROME_LINKS, ...SITE_CHROME_LINKS],
  }),
  component: CoachesCanonical,
});
